import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Translation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { titleize, unescapeHTML, humanize } from 'underscore.string';
import { isEqual, orderBy, first, startCase } from 'lodash';
import _ from 'underscore';
import { uuid } from 'uuidv4';
import {
  IMPORT,
  DOMESTIC,
  EXPORT,
  REPOSITION,
  RELOAD,
} from 'config/order-types';

import { equipmentTypes } from 'constants/EquipmentTypes';
import Multiselect from 'components/v2/multi-select';
import { sluggify } from 'utils/localization/utils';
import formatMoney from 'utils/formatters/Money';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import Alert from 'components/ui/Alert';
import { RAIL, DRAYAGE } from 'constants/ShipmentTypes';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import ShipmentDetailValueItem from './details/ShipmentDetailValueItem';
import ShipmentDetailDateItem from './details/ShipmentDetailDateItem';
import ShipmentDetailDropdown from './details/ShipmentDetailDropDown';

import EditReferenceNumbersModal from './EditReferenceNumbersModal';
import TagEditor from '../../ui/TagEditor';
import Constants from '../../../constants/AdminConstants';
import CargomaticCard from '../../v2/card';
import CargomaticCollapsibleCard from '../../v2/CollapsibleCard';
import DescriptionListItem from '../../v2/DescriptionListItem';
import CargomaticInput from '../../v2/input';
import CargomaticSelect from '../../v2/select';
import InfoIcon from '../../v2/icons/InfoIcon';
import EditButton from '../../v2/buttons/EditButton';
import Badge from '../../v2/Badge';
import { isIntermodalShipmentType } from '../../../helpers/shipment-types';
import {
  LOADED_ON_RAIL,
  DEPARTED_ON_RAIL,
  ARRIVED_AT_RAIL_FACILITY,
  UNLOADED_FROM_RAIL,
  AVAILABLE_AT_RAIL_FACILITY,
  FULLY_OUTGATED_FROM_RAIL_FACILITY,
  FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
  LOADED_AT_RAIL_ORIGIN,
  DEPARTED_FROM_ORIGIN_RAIL,
  ARRIVED_AT_ORIGIN_RAIL_FACILITY,
  UNLOADED_AT_ORIGIN_PORT,
  RAIL_EARLIEST_RETURN_DATE,
  RAIL_CUT_DATE,
} from '../../../helpers/events';
import { importantShipmentDates } from './mappings';
import { getDocuments } from '../../../utils/DocumentsAPIUtils';
import { MASTER_BOL_STATUS } from '../../../constants/Opentrack';
import ShipmentDetailIndividualItem from './details/ShipmentDetailIndividualItem';
import OrderWebAPIUtils from 'utils/OrderWebAPIUtils';
import OrdersStore from 'stores/OrdersStore';

const DELIVERY = 'delivery';
const PICKUP = 'pickup';

const SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS = [
  {
    localizationSlug: 'POD_VALID',
    value: 'POD Valid',
    selectable: true,
  },
  {
    localizationSlug: 'POD_INVALID',
    value: 'POD Invalid',
    selectable: true,
  },
  {
    localizationSlug: 'NEED_VERIFICATION',
    value: 'Needs verification',
    selectable: false,
  },
];

const getDropDownValue = (isPodValid) => {
  const [valid, invalid, needsVerification] =
    SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS;
  if (isPodValid === true) {
    return valid;
  }
  if (isPodValid === false) {
    return invalid;
  }
  return needsVerification;
};

const SERVICE_TYPES = [
  {
    localizationSlug: 'LIVE_LOAD_UNLOAD',
    value: 'Live Load/Unload',
  },
  {
    localizationSlug: 'DROP_HOOK',
    value: 'Drop & Hook',
  },
];
const INTERMODAL_SHIPMENT_TYPES = [
  {
    localizationSlug: 'DRAYAGE',
    value: DRAYAGE,
    titleize: true,
  },
  {
    localizationSlug: 'RAIL',
    value: RAIL,
    titleize: true,
  },
];
const NON_INTERMODAL_SHIPMENT_TYPES = [
  {
    localizationSlug: 'SHORTHAUL',
    value: 'shorthaul',
    titleize: true,
  },
  {
    localizationSlug: 'LONGHAUL',
    value: 'longhaul',
    titleize: true,
  },
  {
    localizationSlug: 'DAYRATE',
    value: 'dayrate',
    titleize: true,
  },
];
const IMPORT_OR_DOMESTIC = [IMPORT, DOMESTIC];
const TOOLTIPS = {
  editReferences: (
    <Tooltip id="tooltip-edit-references">
      <Translation>
        {(t, { i18n }) => (
          <p data-testid="p-63a93803">{t('CLICK_TO_EDIT_REF_NUMS')}</p>
        )}
      </Translation>
    </Tooltip>
  ),
  references: (
    <Tooltip id="tooltip-route-details">
      <Translation>
        {(t, { i18n }) => <p data-testid="p-adea810d">{t('REF_NUMS_NOTE')}</p>}
      </Translation>
    </Tooltip>
  ),
};
const determineServiceTypeValue = (serviceType) => {
  if (serviceType === 'Live Load/Unload') {
    return { service_type: 'live' };
  }
  return { service_type: 'drop' };
};
const determineServiceTypeDisplayValue = (serviceTypeDisplayValue) => {
  if (serviceTypeDisplayValue === 'live') {
    return 'Live Load/Unload';
  }
  return 'Drop & Hook';
};

const determineEditableShipmentTypes = (shipment) => {
  return isIntermodalShipmentType(shipment)
    ? INTERMODAL_SHIPMENT_TYPES
    : NON_INTERMODAL_SHIPMENT_TYPES;
};

const hazmatDescription = (shipment, t) => {
  if (isIntermodalShipmentType(shipment)) {
    const {
      order: { hazmat_description },
    } = shipment;

    return hazmat_description || t('N_A');
  }

  return shipment.hazmat_description ? shipment.hazmat_description : t('N_A');
};

const hazmatClass = (shipment, t) => {
  if (isIntermodalShipmentType(shipment)) {
    const {
      order: { hazmat_class },
    } = shipment;

    return hazmat_class || t('N_A');
  }

  return shipment?.hazmat_class ? shipment.hazmat_class : t('N_A');
};

const hazmatPackagingGroup = (shipment, t) => {
  if (isIntermodalShipmentType(shipment)) {
    const {
      order: { hazmat_packaging_group },
    } = shipment;

    return hazmat_packaging_group || t('N_A');
  }

  return shipment?.hazmat_packaging_group
    ? shipment.hazmat_packaging_group
    : t('N_A');
};

const getOceanCarrierScacLabel = (oceanScacCodes, oceanCarrierScac) => {
  return (
    oceanScacCodes.find(
      (oceanScacCode) => oceanScacCode.value === oceanCarrierScac,
    )?.name || null
  );
};

class ShipmentDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showEditReferenceNumbersModal: false,
      equipment_type: props.shipment.equipment_type || [],
      podDocuments: [],
      missingReferenceNumbersList: [],
      ocean_scacs: [],
      order: null,
    };
  }

  retrieveOceanScacs = async () => {
    const ocean_scacs = await OrderWebAPIUtils.getCarrierScacs();
    if (ocean_scacs) {
      this.setState({
        ocean_scacs: ocean_scacs.OCEAN_SCAC_CODES.map((scac) => ({
          name: scac.carrier,
          value: scac.ocean_scac,
        })),
      });
    }
  };

  async componentDidMount() {
    try {
      const podDocuments = await this.getDocuments(this.props.shipment._id);

      let missingReferenceNumbersList = [];
      if (
        featureEnabled(CONFIGURED_FEATURES.SSR) &&
        isIntermodalShipmentType(this.props.shipment)
      ) {
        const { missingReferenceNumbers = [] } =
          await ShipmentWebAPIUtils.getMissingReferenceNumbersList(
            this.props.shipment,
          );
        missingReferenceNumbersList = missingReferenceNumbers;
      }

      this.setState(
        {
          podDocuments: podDocuments.filter(
            ({ status }) => status === 'UPLOADED',
          ),
          missingReferenceNumbersList,
        },
        () => {
          if (this.props.updateMissingReferenceNumbers) {
            this.props.updateMissingReferenceNumbers(
              this.state.missingReferenceNumbersList,
            );
          }
        },
      );

      if (isIntermodalShipmentType(this.props.shipment)) {
        await this.retrieveOceanScacs();

        const order = OrdersStore.getOrder();
        this.setState({ order });
      }
    } catch (e) {
      console.log(e);
    }
  }

  async componentDidUpdate(prevProps) {
    const prevReferenceNumbers = prevProps.shipment.reference_numbers;
    const currReferenceNumbers = this.props.shipment.reference_numbers;
    try {
      if (
        featureEnabled(CONFIGURED_FEATURES.SSR) &&
        !isEqual(prevReferenceNumbers, currReferenceNumbers)
      ) {
        const { missingReferenceNumbers = [] } =
          await ShipmentWebAPIUtils.getMissingReferenceNumbersList(
            this.props.shipment,
          );

        this.setState(
          {
            missingReferenceNumbersList: missingReferenceNumbers,
          },
          () => {
            if (this.props.updateMissingReferenceNumbers) {
              this.props.updateMissingReferenceNumbers(
                this.state.missingReferenceNumbersList,
              );
            }
          },
        );
      }
    } catch (error) {
      console.log(error);
    }
  }

  getDocuments = async (shipmentId) => {
    try {
      const response = await getDocuments([shipmentId], 'proof_of_delivery');
      return response.data?.getDocuments?.documents || [];
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  getDeliveryOrderStatus = () => {
    const { shipment, t } = this.props;
    const { delivery_order_status } = shipment;

    if (shipment) {
      return delivery_order_status === 'not-sent' || !delivery_order_status ? (
        <Badge dataTestId="Badge-f03af3d8" color="red">
          {t('NOT_SENT')}
        </Badge>
      ) : (
        <Badge dataTestId="Badge-439e12b0" color="brand">
          {t(sluggify(delivery_order_status))}
        </Badge>
      );
    }
  };

  handleEditReferenceNumbers = () =>
    this.setState({ showEditReferenceNumbersModal: true });
  handleCloseEditReferenceNumbersModal = () =>
    this.setState({ showEditReferenceNumbersModal: false });
  handleSaveReferenceNumbers = (referenceNumbers, updatedRefEvents = []) => {
    this.props.onReferenceNumbersChange(
      this.props.shipment,
      referenceNumbers,
      updatedRefEvents,
    );
    this.handleCloseEditReferenceNumbersModal();
  };

  getMasterBolBadgeData = () => {
    const {
      shipment: { opentrack_metadata },
      t,
    } = this.props;
    if (opentrack_metadata?.master_bol_status) {
      const { master_bol_status: masterBolStatus } = opentrack_metadata;
      if (masterBolStatus === MASTER_BOL_STATUS.CONFRIMED) {
        return {
          badgeInfo: t('CONFIRMED'),
          badgeColor: 'green',
        };
      }
      return {
        badgeInfo: t(masterBolStatus.toUpperCase()),
        badgeColor: 'red',
      };
    }
    return {};
  };

  renderReferenceNumbers() {
    const { shipment, t } = this.props;
    const { reference_numbers, order } = shipment;
    const orderType = order?.order_type;

    if (!reference_numbers || reference_numbers.length === 0) {
      return <p data-testid="p-c5d8db35">{t('NO_REFERENCE_NUMBERS')}</p>;
    }

    const refNumList = reference_numbers
      .filter((refNum) => !refNum.stop_id)
      .map((refNum, i) => {
        const { name, value } = refNum;
        const label = t(sluggify(unescapeHTML(name)));
        const descriptionListItemProps = {
          dataTestId: `DescriptionListItem-26766dd0-${i}`,
          key: i,
          className: 'mb-4',
          label,
        };

        // Badge for Master BOL Validation
        if (
          name === 'master_bol' &&
          isIntermodalShipmentType(shipment) &&
          orderType !== 'export'
        ) {
          const { badgeColor, badgeInfo } = this.getMasterBolBadgeData();
          return (
            <DescriptionListItem
              {...descriptionListItemProps}
              badgeColor={badgeColor}
              badgeInfo={badgeInfo}
            >
              {unescapeHTML(value)}
            </DescriptionListItem>
          );
        }

        return (
          <DescriptionListItem {...descriptionListItemProps}>
            {unescapeHTML(value)}
          </DescriptionListItem>
        );
      });

    return (
      <div
        data-testid="div-2f87f7b9"
        className="sm:grid sm:grid-cols-2 sm:gap-4"
      >
        {refNumList}
      </div>
    );
  }

  renderMissingReferenceNumbers() {
    const { t } = this.props;
    if (this.state.missingReferenceNumbersList.length) {
      return (
        <Alert
          dataTestId="Alert-8503592c"
          title={t('PLEASE_PROVIDE_REFERENCE_NUMBERS')}
        >
          <ul data-testid="ul-25265778">
            {this.state.missingReferenceNumbersList.map(
              (missingReferenceNumber, index) => (
                <li
                  data-testid={`li-417281a9-${index}`}
                  key={missingReferenceNumber}
                >
                  {t(missingReferenceNumber?.toUpperCase())}
                </li>
              ),
            )}
          </ul>
        </Alert>
      );
    }
  }

  renderOrderLink() {
    const { shipment, t } = this.props;

    if (!shipment.order || !shipment.order._id) {
      return null;
    }

    const orderLink = `/orders/${shipment.order._id}`;
    return (
      <DescriptionListItem
        dataTestId="DescriptionListItem-a4bd4d92"
        label={t('ORDER')}
        className="sm:col-span-1"
      >
        <a
          data-testid="a-726d2d57"
          href={orderLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 cursor-pointer hover:text-blue-600"
        >
          <span data-testid="span-5b9a35c0">{shipment.order.order_id}</span>
        </a>
      </DescriptionListItem>
    );
  }

  handleEquipmentTypeChange = (equipment_types) => {
    const equipment_type = equipment_types.map((eq) => eq.slug);
    this.setState({ equipment_type });
  };

  handleEquipmentTypeSave = () => {
    const { equipment_type } = this.state;
    this.props.onEquipmentTypeChange(equipment_type);
  };

  renderEquipmentTypes() {
    const { t } = this.props;
    const equipment_types = equipmentTypes.map((type) => {
      return {
        name: t(type.label),
        slug: type.label,
        selected: this.state.equipment_type.indexOf(type.label) > -1,
      };
    });
    return (
      <div data-testid="div-6091c9a9" className="mt-2">
        <Multiselect
          dataTestId="Multiselect-74d07a16"
          label={t('SELECT_EQUIPMENT_TYPES')}
          items={equipment_types}
          valueKey="name"
          onSelect={this.handleEquipmentTypeChange}
        />
        <div data-testid="div-2d77fc7b" className="flex flex-row justify-end">
          <button
            data-testid="button-c3393514"
            onClick={this.handleEquipmentTypeSave}
            className="mt-2 button button-green button-small"
          >
            {titleize(t('SAVE'))}
          </button>
        </div>
      </div>
    );
  }

  getOnHold() {
    const { holds } = this.props.shipment;
    if (!holds || !holds.length) {
      return null;
    }

    const unresolvedHolds = holds.filter((hold) => {
      return !hold.resolved_at;
    });
    const lastUnresolved = _.last(unresolvedHolds);
    return lastUnresolved?.reported_at;
  }

  renderImportantDates() {
    const { shipment, t } = this.props;
    return (
      <CargomaticCollapsibleCard
        dataTestId="CargomaticCollapsibleCard-1c577a2a"
        className="mb-6"
        header={() => (
          <div
            data-testid="div-dda0e314"
            className="flex items-center justify-between"
          >
            <div
              data-testid="div-3f1f4a2e"
              className="flex items-center space-x-1"
            >
              <h3 data-testid="h3-eef0d926" className="my-0">
                {t('IMPORTANT_DATES')}
              </h3>
            </div>
          </div>
        )}
      >
        <div
          data-testid="div-85a8eda4"
          className="sm:grid sm:grid-cols-2 sm:gap-6"
        >
          {importantShipmentDates.map(({ slug, localizationSlug }) => (
            <ShipmentDetailDateItem
              dataTestId={`ShipmentDetailDateItem-${uuid()}`}
              className="mb-4 sm:col-span-1"
              title={t(`${localizationSlug}`)}
              key={humanize(`${localizationSlug}`)}
              heapClassName="shipment-shipmentDetails-editRequestedPickupDate heap"
              value={shipment?.[slug]}
              editable
              onSave={this.props.onUpdateImportantDates}
              param={slug}
              useNamedUpdate
            />
          ))}
        </div>
      </CargomaticCollapsibleCard>
    );
  }

  getUpdateStatusBadge(eventName) {
    const { audits, shipment } = this.props;

    if (!shipment[eventName] || !audits || !audits.length) {
      return null;
    }

    const events = audits.filter((audit) => {
      const { event } = audit;
      if (event === eventName || event === `${eventName}_changed`) {
        return audit;
      }
      return false;
    });

    if (!events.length) {
      return null;
    }
    const mostRecentUpdate = first(orderBy(events, ['timestamp'], ['desc']));
    const { user } = mostRecentUpdate;
    if (
      user._type === 'Admin' &&
      user.email === 'integrations@cargomatic.com'
    ) {
      return 'OPENTRACK';
    }
    if (user._type === 'Admin') {
      return 'ADMIN';
    }
    if (user._type === 'Carrier') {
      return 'CARRIER';
    }
    return null;
  }

  getNonInterModalDetailCard() {
    const { shipment, t } = this.props;
    const podUploaded = !!this.state.podDocuments.length;
    const isIntermodalType = isIntermodalShipmentType(shipment);

    return (
      <CargomaticCard dataTestId="CargomaticCard-b9f397db">
        <div
          data-testid="div-bf5ec193"
          className="sm:grid sm:grid-cols-2 sm:gap-6"
        >
          {shipment.order && shipment.order._id && this.renderOrderLink()}
          <DescriptionListItem
            dataTestId="DescriptionListItem-11f49bcd"
            label={t('B2B')}
            className="mb-4 sm:col-span-1"
          >
            {shipment.business ? t('YES') : t('NO')}
          </DescriptionListItem>

          <DescriptionListItem
            dataTestId="DescriptionListItem-2ca59407"
            label={t('AUTO_EXPOSURE')}
            className="mb-4 sm:col-span-1"
          >
            {shipment.hold_at_dispatch ? t('DISABLED') : t('ENABLED')}
          </DescriptionListItem>
          <CargomaticInput
            dataTestId="CargomaticInput-3977d2ba"
            className="mb-4 sm:col-span-1"
            type="checkbox"
            label={t('EXCEPTION_EXEMPT')}
            id="exception_exempt"
            name="exception_exempt"
            checked={this.props.shipment.exception_exempt}
            onChange={() => {
              this.props.onExceptionExemptChange(
                !this.props.shipment.exception_exempt,
              );
            }}
          />
          {shipment.order && (
            <>
              <DescriptionListItem
                dataTestId="DescriptionListItem-4aef2fa0"
                label={t('VESSEL')}
                className="mb-4 sm:col-span-1"
              >
                {shipment.order.vessel_or_rail}
              </DescriptionListItem>
              <DescriptionListItem
                dataTestId="DescriptionListItem-f1ad50f5"
                label={startCase(t('TRAIN'))}
                className="mb-4 sm:col-span-1"
              >
                {shipment.order.train}
              </DescriptionListItem>
            </>
          )}
          <CargomaticSelect
            dataTestId="CargomaticSelect-75f0f4fb"
            className="mb-4 sm:col-span-1"
            label={t('SHIPMENT_TYPE')}
            items={determineEditableShipmentTypes(shipment)}
            textKey="localizationSlug"
            valueKey="value"
            disabled={isIntermodalType}
            value={this.props.shipment.shipment_type}
            onSelect={(item) => this.props.onShipmentTypeChange(item.value)}
          />

          {isIntermodalType && (
            <CargomaticSelect
              dataTestId="CargomaticSelect-328b1a50"
              className="mb-4 sm:col-span-1"
              label={t('SERVICE_TYPE')}
              items={SERVICE_TYPES}
              textKey="localizationSlug"
              valueKey="value"
              value={determineServiceTypeDisplayValue(
                this.props.shipment.order.service_type,
              )}
              onSelect={(item) =>
                this.props.onUpdateServiceType(
                  determineServiceTypeValue(item?.value),
                )
              }
            />
          )}

          <div data-testid="div-addfddc1" className="equipment-type">
            <DescriptionListItem
              dataTestId="DescriptionListItem-1243a35c"
              className="sm:col-span-1"
              label={t('EQUIPMENT_TYPES')}
            >
              <div data-testid="div-b3cf7019">
                {this.renderEquipmentTypes()}
              </div>
            </DescriptionListItem>
          </div>

          <DescriptionListItem
            dataTestId="DescriptionListItem-8beb13ea"
            className="mb-4 sm:col-span-1"
            label={t('POD_VERIFICATION')}
          >
            {podUploaded && (
              <div data-testid="div-6da3cbfa" className="flex flex-row">
                <ShipmentDetailDropdown
                  className="mb-4 sm:col-span-1 w-48"
                  items={SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS}
                  disabled={!podUploaded}
                  value={getDropDownValue(shipment.is_pod_valid)}
                  onSave={this.props.onPODValidChange}
                  editable
                  showIcon
                  iconValue={shipment.is_pod_valid}
                />
              </div>
            )}
            {!podUploaded && (
              <span data-testid="span-e33a2e5b">{t('MISSING_POD')}</span>
            )}
          </DescriptionListItem>

          <DescriptionListItem
            dataTestId="DescriptionListItem-c7808fc0"
            className="mb-4 sm:col-span-1"
            label={t('DELIVERY_ORDER_STATUS')}
          >
            {this.getDeliveryOrderStatus()}
          </DescriptionListItem>
          {isIntermodalType && (
            <DescriptionListItem
              dataTestId="DescriptionListItem-d5dc73fb"
              className="mb-4 sm:col-span-1"
              label={t('HAZMAT_CLASS')}
            >
              {hazmatClass(shipment, t)}
            </DescriptionListItem>
          )}
          {isIntermodalType && (
            <DescriptionListItem
              dataTestId="DescriptionListItem-37fef391"
              className="mb-4 sm:col-span-1"
              label={t('HAZMAT_PACKAGING_GROUP')}
            >
              {hazmatPackagingGroup(shipment, t)}
            </DescriptionListItem>
          )}
          <DescriptionListItem
            dataTestId="DescriptionListItem-c17c006a"
            className="mb-4 sm:col-span-1"
            label={t('HAZMAT_DESCRIPTION')}
          >
            {hazmatDescription(shipment, t)}
          </DescriptionListItem>
        </div>
      </CargomaticCard>
    );
  }

  getInterModalShipmentOrderAndAdditionalCards() {
    const { shipment, t } = this.props;
    const podUploaded = !!this.state.podDocuments.length;
    const isIntermodalType = isIntermodalShipmentType(shipment);

    return (
      <>
        <CargomaticCollapsibleCard
          dataTestId="CargomaticCollapsibleCard-1c577a1b"
          className="mb-6"
          header={() => (
            <div
              data-testid="div-dda0e324"
              className="flex items-center justify-between"
            >
              <div
                data-testid="div-3f1f4a3e"
                className="flex items-center space-x-1"
              >
                <h3 data-testid="h3-eef0d934" className="my-0">
                  {t('ORDER_DETAILS')}
                </h3>
              </div>
            </div>
          )}
        >
          <div
            data-testid="div-85a8edb4"
            className="sm:grid sm:grid-cols-2 sm:gap-6"
          >
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a8d3"
              title={t('ORDER')}
              value={shipment.order.order_id}
              href={`/orders/${shipment.order._id} `}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a8d4"
              title={t('ORDER_TYPE')}
              value={humanize(this.state.order?.order_type)}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a8d5"
              title={t('MODE')}
              value={humanize(this.state.order?.mode?.toUpperCase())}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be336"
              title={t('MOVEMENT_TYPE')}
              value={humanize(this.state.order?.movement_type)}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be347"
              title={t('SHIPMENT_TYPE')}
              value={humanize(shipment.shipment_type)}
            />
            {isIntermodalType && (
              <CargomaticSelect
                dataTestId="CargomaticSelect-328b1a50"
                className="mb-4 sm:col-span-1"
                label={t('SERVICE_TYPE')}
                items={SERVICE_TYPES}
                textKey="localizationSlug"
                valueKey="value"
                value={determineServiceTypeDisplayValue(
                  this.props.shipment.order.service_type,
                )}
                onSelect={(item) =>
                  this.props.onUpdateServiceType(
                    determineServiceTypeValue(item?.value),
                  )
                }
              />
            )}
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be34f"
              title={t('STEAMSHIP_RAILROAD')}
              value={getOceanCarrierScacLabel(
                this.state?.ocean_scacs,
                this.state?.order?.ocean_carrier_scac,
              )}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be43d"
              title={t('B2B')}
              value={shipment.business ? 'YES' : 'NO'}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be330"
              title={t('VESSEL')}
              value={this.state.order?.vessel_or_rail}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be378"
              title={titleize(t('TRAIN'))}
              value={this.state.order?.train}
            />
          </div>
        </CargomaticCollapsibleCard>

        <CargomaticCollapsibleCard
          dataTestId="CargomaticCollapsibleCard-1c577a2c"
          className="mb-6"
          header={() => (
            <div
              data-testid="div-dda0e336"
              className="flex items-center justify-between"
            >
              <div
                data-testid="div-3f1f4a4e"
                className="flex items-center space-x-1"
              >
                <h3 data-testid="h3-eef0d967" className="my-0">
                  {t('ADDITIONAL_DETAILS')}
                </h3>
              </div>
            </div>
          )}
        >
          <div
            data-testid="div-85a8edc3"
            className="sm:grid sm:grid-cols-2 sm:gap-6"
          >
            <div className="max-w-1/2">
              <ShipmentDetailIndividualItem
                dataTestId="ShipmentDetailIndividualItem-9074a8f6"
                title={t('EQUIPMENT_TYPES')}
                value={this.renderEquipmentTypes()}
              />
            </div>

            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a83e"
              title={t('POD_VERIFICATION')}
              value={
                <>
                  {podUploaded && (
                    <div data-testid="div-6da3cbc7" className="flex flex-row">
                      <ShipmentDetailDropdown
                        className="mb-4 sm:col-span-1 w-48"
                        items={SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS}
                        disabled={!podUploaded}
                        value={getDropDownValue(shipment.is_pod_valid)}
                        onSave={this.props.onPODValidChange}
                        editable
                        showIcon
                        iconValue={shipment.is_pod_valid}
                      />
                    </div>
                  )}
                  {!podUploaded && (
                    <span data-testid="span-e33a2e5b">{t('MISSING_POD')}</span>
                  )}
                </>
              }
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a8f7"
              title={t('DELIVERY_ORDER_STATUS')}
              value={this.getDeliveryOrderStatus()}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be33e"
              title={t('HAZMAT_CLASS')}
              value={hazmatClass(shipment, t)}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-651be34e"
              title={t('HAZMAT_PACKAGING_GROUP')}
              value={hazmatPackagingGroup(shipment, t)}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-653fe34e"
              title={t('HAZMAT_DESCRIPTION')}
              value={hazmatDescription(shipment, t)}
            />
            <CargomaticInput
              dataTestId="CargomaticInput-3977d2be"
              className="mb-4 sm:col-span-1"
              type="checkbox"
              label={t('EXCEPTION_EXEMPT')}
              id="exception_exempt"
              name="exception_exempt"
              checked={this.props.shipment.exception_exempt}
              onChange={() => {
                this.props.onExceptionExemptChange(
                  !this.props.shipment.exception_exempt,
                );
              }}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-381be34e"
              title={t('AUTO_EXPOSURE')}
              value={shipment.hold_at_dispatch ? t('DISABLED') : t('ENABLED')}
            />
          </div>
        </CargomaticCollapsibleCard>
      </>
    );
  }

  render() {
    const { shipment, t, audits } = this.props;
    const isIntermodalType = isIntermodalShipmentType(shipment);
    const shouldShowDatesForImportOrDomestic =
      shipment.order && IMPORT_OR_DOMESTIC.includes(shipment.order.order_type);
    const shouldShowDatesForRepositionDrayage =
      shipment.order &&
      REPOSITION === shipment.order.order_type &&
      DRAYAGE === shipment.shipment_type;
    const shouldShowDatesForRepositionRail =
      shipment.order &&
      REPOSITION === shipment.order.order_type &&
      RAIL === shipment.shipment_type;
    const shouldShowDatesForExports =
      shipment.order && EXPORT === shipment.order.order_type;
    const shouldShowRailPerDiemLFD =
      isIntermodalType &&
      shipment.order.order_type !== EXPORT &&
      shipment.order.order_type !== RELOAD &&
      shipment.order_movement_type !== PICKUP;

    const podUploaded = !!this.state.podDocuments.length;

    return (
      <div data-testid="div-2cc53f08" className="space-y-6">
        {isIntermodalType && (
          <CargomaticCollapsibleCard
            dataTestId="CargomaticCollapsibleCard-5a0b41e6"
            className="mb-6"
            header={() => (
              <div
                data-testid="div-5e58c364"
                className="flex items-center justify-between"
              >
                <div
                  data-testid="div-0ebc6813"
                  className="flex items-center space-x-1"
                >
                  <h3 data-testid="h3-4e159bc8" className="my-0">
                    {t('TERMINAL_EVENTS')}
                  </h3>
                </div>
              </div>
            )}
          >
            <div
              data-testid="div-bbed7891"
              className="sm:grid sm:grid-cols-2 sm:gap-6"
            >
              {shipment.port_of_discharge ? (
                <DescriptionListItem
                  dataTestId="DescriptionListItem-ff4fe472"
                  label={t('PORT_OF_DISCHARGE')}
                  className="mb-4 sm:col-span-1"
                >
                  {shipment.port_of_discharge}
                </DescriptionListItem>
              ) : null}
              {shipment.terminal_at_port_of_discharge ? (
                <DescriptionListItem
                  dataTestId="DescriptionListItem-9da72169"
                  label={titleize(t('TERMINAL'))}
                  className="mb-4 sm:col-span-1"
                >
                  {shipment.terminal_at_port_of_discharge}
                </DescriptionListItem>
              ) : null}
              {shouldShowDatesForImportOrDomestic && [
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-f70d5c6a"
                  className="mb-4 sm:col-span-1"
                  title={t('ETA')}
                  key="ETA"
                  heapClassName="shipment-shipmentDetails-editETA heap"
                  value={shipment.estimated_arrival}
                  editable
                  onSave={this.props.onUpdateEstimatedArrivalChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-14e83387"
                  className="mb-4 sm:col-span-1"
                  title={t('ARRIVAL_DATE')}
                  key="Arrival Date"
                  heapClassName="shipment-shipmentDetails-editArrivalDate heap"
                  value={shipment.arrival_date}
                  editable
                  onSave={this.props.onUpdateArrivalDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-53ca124b"
                  className="mb-4 sm:col-span-1"
                  title={t('DISCHARGE_DATE')}
                  key="Discharge Date"
                  heapClassName="shipment-shipmentDetails-editDischargeDate heap"
                  value={shipment.discharge_date}
                  editable
                  onSave={this.props.onUpdateDischargeDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-f3f1ded6"
                  className="mb-4 sm:col-span-1"
                  title={t('TERMINAL_AVAILABLE_DATE')}
                  key="Terminal Available Date"
                  value={shipment.available_date}
                  heapClassName="shipment-shipmentDetails-editAvailableDate heap"
                  editable
                  onSave={this.props.onUpdateAvailableDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-2869a6be"
                  className="mb-4 sm:col-span-1"
                  title={titleize(t('TERMINAL_LAST_FREE_DATE'))}
                  key="Last Free Date"
                  value={shipment.last_free_date}
                  heapClassName="shipment-shipmentDetails-editLastFreeDate heap"
                  editable
                  onSave={this.props.onUpdateLastFreeDateChange}
                  badge={
                    shipment.last_free_day_not_reported_by_terminal
                      ? 'NOT_REPORTED'
                      : null
                  }
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-dc46686a"
                  className="mb-4 sm:col-span-1"
                  title={t('OUTGATED_DATE')}
                  key="Outgated Date"
                  value={shipment.outgated_date}
                  heapClassName="shipment-shipmentDetails-editOutgatedDate heap"
                  editable
                  onSave={this.props.onUpdateOutgatedDateChange}
                />,

                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-21a8d7c0"
                  className="mb-4 sm:col-span-1"
                  title={t('EMPTY_RETURNED_DATE')}
                  key="Empty Returned Date"
                  heapClassName="shipment-shipmentDetails-editEmptyReturnedDate heap"
                  value={shipment.empty_returned_date}
                  editable
                  onSave={this.props.onUpdateEmptyReturnedDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-6c7ef0b2"
                  className="mb-4 sm:col-span-1"
                  title="On Hold"
                  key="On Hold"
                  heapClassName="shipment-shipmentDetails-editEmptyReturnedDate heap"
                  value={this.getOnHold()}
                />,
              ]}
              {shouldShowDatesForImportOrDomestic &&
                shipment.order_movement_type !== PICKUP && [
                  <ShipmentDetailDateItem
                    dataTestId="ShipmentDetailDateItem-8450bdb9"
                    className="mb-4 sm:col-span-1"
                    title={t('EMPTY_READY_FOR_PICKUP_DATE')}
                    key="Empty Ready for Pickup Date"
                    heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                    value={shipment.empty_ready_for_pickup}
                    editable
                    onSave={this.props.onUpdateEmptyReadyForPickupDateChange}
                  />,
                  <ShipmentDetailDateItem
                    dataTestId="ShipmentDetailDateItem-590ca159"
                    className="mb-4 sm:col-span-1"
                    title={t('PER_DIEM_LFD')}
                    key="Per Diem Last Free Date"
                    heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                    value={shipment.per_diem_lfd}
                    editable
                    onSave={this.props.onUpdatePerDiemDateChange}
                  />,
                ]}
              {shouldShowDatesForExports && [
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-99e3e47a"
                  className="mb-4 sm:col-span-1"
                  title={t('EARLIEST_DATE_TO_GET_EMPTY')}
                  key="Earliest Date to Get Empty"
                  heapClassName="shipment-shipmentDetails-editEmptyReturnedDate heap"
                  value={shipment.earliest_empty_date}
                  editable
                  onSave={this.props.onUpdateEarliestEmptyDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-3860e949"
                  className="mb-4 sm:col-span-1"
                  title={t('EARLIEST_RETURN_DATE')}
                  key="Earliest Return Date"
                  heapClassName="shipment-shipmentDetails-editEarliestReturnDate heap"
                  value={shipment.earliest_return_date}
                  editable
                  onSave={this.props.onUpdateEarliestReturnDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-237f4a02"
                  className="mb-4 sm:col-span-1"
                  title={t('DOCUMENT_CUTOFF_DATE')}
                  key="Document Cutoff Date"
                  heapClassName="shipment-shipmentDetails-editDocumentCutoffDate heap"
                  value={shipment.document_cutoff_date}
                  editable
                  onSave={this.props.onUpdateDocumentCutoffDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-ba1e85b1"
                  className="mb-4 sm:col-span-1"
                  title={t('LAST_CUT_DATE')}
                  key="Last Cut Date"
                  heapClassName="shipment-shipmentDetails-editLastCutDate heap"
                  value={shipment.last_cut_date}
                  editable
                  onSave={this.props.onUpdateLastCutDateChange}
                />,
              ]}
              {shouldShowDatesForRepositionDrayage &&
                shipment.order_movement_type !== PICKUP && [
                  <ShipmentDetailDateItem
                    dataTestId="ShipmentDetailDateItem-6ffdc980"
                    className="mb-4 sm:col-span-1"
                    title={t('EMPTY_READY_FOR_PICKUP_DATE')}
                    key="Empty Ready for Pickup Date"
                    heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                    value={shipment.empty_ready_for_pickup}
                    editable
                    onSave={this.props.onUpdateEmptyReadyForPickupDateChange}
                  />,
                  <ShipmentDetailDateItem
                    dataTestId="ShipmentDetailDateItem-36bd1c98"
                    className="mb-4 sm:col-span-1"
                    title={t('PER_DIEM_LFD')}
                    key="Per Diem Last Free Date"
                    heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                    value={shipment.per_diem_lfd}
                    editable
                    onSave={this.props.onUpdatePerDiemDateChange}
                  />,
                ]}
              {shouldShowDatesForRepositionRail &&
                shipment.order_movement_type !== PICKUP && [
                  <ShipmentDetailDateItem
                    dataTestId="ShipmentDetailDateItem-896da70b"
                    className="mb-4 sm:col-span-1"
                    title={t('EMPTY_READY_FOR_PICKUP_DATE')}
                    key="Empty Ready for Pickup Date"
                    heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                    value={shipment.empty_ready_for_pickup}
                    editable
                    onSave={this.props.onUpdateEmptyReadyForPickupDateChange}
                  />,
                  <ShipmentDetailDateItem
                    dataTestId="ShipmentDetailDateItem-0cfa8a11"
                    className="mb-4 sm:col-span-1"
                    title={t('PER_DIEM_LFD')}
                    key="Per Diem Last Free Date"
                    heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                    value={shipment.per_diem_lfd}
                    editable
                    onSave={this.props.onUpdatePerDiemDateChange}
                  />,
                ]}
            </div>
          </CargomaticCollapsibleCard>
        )}
        {isIntermodalType && (
          <CargomaticCollapsibleCard
            dataTestId="CargomaticCollapsibleCard-1e617adc"
            className="mb-6"
            header={() => (
              <div
                data-testid="div-4bf421c3"
                className="flex items-center justify-between"
              >
                <div
                  data-testid="div-1a517bdb"
                  className="flex items-center space-x-1"
                >
                  <h3 data-testid="h3-b0294917" className="my-0">
                    {t('YARD_EVENTS')}
                  </h3>
                </div>
              </div>
            )}
          >
            <div
              data-testid="div-6a7268a6"
              className="sm:grid sm:grid-cols-2 sm:gap-6"
            >
              {shouldShowDatesForImportOrDomestic && [
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-cd30cc72"
                  className="mb-4 sm:col-span-1"
                  title={t('YARD_AVAILABLE_DATE')}
                  key="Yard Available Date"
                  heapClassName="shipment-shipmentDetails-editYardAvailableDate heap"
                  value={shipment.yard_available_date}
                  editable
                  onSave={this.props.onUpdateYardAvailableDateChange}
                />,
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-c2b8d7ea"
                  className="mb-4 sm:col-span-1"
                  title={t('YARD_LAST_FREE_DATE')}
                  key="Yard Last Free Date"
                  value={shipment.yard_last_free_date}
                  heapClassName="shipment-shipmentDetails-editLastFreeDate heap"
                  editable
                  onSave={this.props.onUpdateYardLastFreeDateChange}
                />,
              ]}
              <ShipmentDetailValueItem
                dataTestId="ShipmentDetailValueItem-4fa72f1b"
                title={t('STACK_POSITION')}
                value={unescapeHTML(shipment.stack_position)}
                editable
                onSave={(value) =>
                  this.props.onStackPositionUpdate(shipment, value)
                }
              />
            </div>
          </CargomaticCollapsibleCard>
        )}
        {isIntermodalType && (
          <CargomaticCollapsibleCard
            dataTestId="CargomaticCollapsibleCard-c0d8f246"
            className="mb-6"
            header={() => (
              <div
                data-testid="div-c6bdc4b7"
                className="flex items-center justify-between"
              >
                <div
                  data-testid="div-2d9977cf"
                  className="flex items-center space-x-1"
                >
                  <h3 data-testid="h3-ba67f981" className="my-0">
                    {t('RAIL_EVENTS')}
                  </h3>
                </div>
              </div>
            )}
          >
            <div
              data-testid="div-16805485"
              className="sm:grid sm:grid-cols-2 sm:gap-6"
            >
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-67774011"
                className="mb-4 sm:col-span-1"
                title={t('EMPTY_RAIL_BILLING')}
                key="Empty Rail Billing"
                heapClassName="shipment-shipmentDetails-editEmptyRailBillingDate heap"
                value={shipment?.empty_rail_billing}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="empty_rail_billing"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-c5ab0e0c"
                className="mb-4 sm:col-span-1"
                title={t('LOADED_RAIL_BILLING')}
                key="Loaded Rail Billing"
                heapClassName="shipment-shipmentDetails-editLoaderRailBillingDate heap"
                value={shipment?.loaded_rail_billing}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="loaded_rail_billing"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-e3750e78"
                className="mb-4 sm:col-span-1"
                title={t('LFD_AT_RAIL_FACILITY_ESTIMATED')}
                key="LFD At Rail Facility"
                heapClassName="shipment-shipmentDetails-editLFDAtRailFacilityDate heap"
                value={shipment.rail_lfd_at_facility_estimated}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_lfd_at_facility_estimated"
                badge={this.getUpdateStatusBadge(
                  'rail_lfd_at_facility_estimated',
                )}
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-859e82fe"
                className="mb-4 sm:col-span-1"
                title={t('LFD_AT_RAIL_FACILITY')}
                key="LFD At Rail Facility"
                heapClassName="shipment-shipmentDetails-editLFDAtRailFacilityDate heap"
                value={shipment.rail_lfd_at_facility}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_lfd_at_facility"
                useNamedUpdate
                badge={this.getUpdateStatusBadge('rail_lfd_at_facility')}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-72b78af2"
                className="mb-4 sm:col-span-1"
                title={t('ETA_TO_FINAL_RAIL_FACILITY')}
                key="ETA To Final Rail Facilty"
                heapClassName="shipment-shipmentDetails-editETAToFinalRailFaciltyDate heap"
                value={shipment.rail_eta_final_facility}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_eta_final_facility"
                useNamedUpdate
              />
              {shouldShowRailPerDiemLFD && (
                <ShipmentDetailDateItem
                  dataTestId="ShipmentDetailDateItem-cd825ecc"
                  className="mb-4 sm:col-span-1"
                  title={t('PER_DIEM_LFD')}
                  key="Rail Per Diem Last Free Date"
                  heapClassName="shipment-shipmentDetails-editEmptyReadyForPickupDate heap"
                  value={shipment.rail_per_diem_lfd}
                  editable
                  onSave={this.props.onUpdateRailEvent}
                  param="rail_per_diem_lfd"
                  useNamedUpdate
                />
              )}
            </div>
            <div data-testid="div-765d2ea9" className="my-5 text-2xl font-bold">
              {t('IMPORT')}
            </div>
            <div
              data-testid="div-d3a633cc"
              className="sm:grid sm:grid-cols-2 sm:gap-6"
            >
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-4a773285"
                className="mb-4 sm:col-span-1"
                title={t(LOADED_ON_RAIL)}
                key={t(LOADED_ON_RAIL)}
                heapClassName="shipment-shipmentDetails-editLoadedOnRailDate heap"
                value={shipment.rail_loaded}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_loaded"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-fd98d812"
                className="mb-4 sm:col-span-1"
                title={t(DEPARTED_ON_RAIL)}
                key={t(DEPARTED_ON_RAIL)}
                heapClassName="shipment-shipmentDetails-editDepartedOnRailDate heap"
                value={shipment.rail_departed}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_departed"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-d4dcc803"
                className="mb-4 sm:col-span-1"
                title={t(ARRIVED_AT_RAIL_FACILITY)}
                key={t(ARRIVED_AT_RAIL_FACILITY)}
                heapClassName="shipment-shipmentDetails-editArrivedAtRailFacilityDate heap"
                value={shipment.rail_arrived_at_facility}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_arrived_at_facility"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-36f5646a"
                className="mb-4 sm:col-span-1"
                title={t(UNLOADED_FROM_RAIL)}
                key={t(UNLOADED_FROM_RAIL)}
                value={shipment.rail_unloaded}
                heapClassName="shipment-shipmentDetails-editUnloadedFromRailDate heap"
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_unloaded"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-6cbb1385"
                className="mb-4 sm:col-span-1"
                title={t(AVAILABLE_AT_RAIL_FACILITY)}
                key={t(AVAILABLE_AT_RAIL_FACILITY)}
                value={shipment.rail_available_at_facility}
                heapClassName="shipment-shipmentDetails-editAvailableAtRailFacilityDate heap"
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_available_at_facility"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-dfefbff9"
                className="mb-4 sm:col-span-1"
                title={t(FULLY_OUTGATED_FROM_RAIL_FACILITY)}
                key={t(FULLY_OUTGATED_FROM_RAIL_FACILITY)}
                value={shipment.rail_outgated_from_facility}
                heapClassName="shipment-shipmentDetails-editFullOutgatedFromRailFacilityDate heap"
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_outgated_from_facility"
                useNamedUpdate
              />
            </div>
            <div data-testid="div-fcb8e70d" className="my-5 text-2xl font-bold">
              {t('EXPORT')}
            </div>
            <div
              data-testid="div-ebb5d58e"
              className="sm:grid sm:grid-cols-2 sm:gap-6"
            >
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-9efadd70"
                className="mb-4 sm:col-span-1"
                title={t(RAIL_EARLIEST_RETURN_DATE)}
                key={t(RAIL_EARLIEST_RETURN_DATE)}
                heapClassName="shipment-shipmentDetails-editRailEarliestReturnDate heap"
                value={shipment.rail_earliest_return}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_earliest_return"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-228468f6"
                className="mb-4 sm:col-span-1"
                title={t(FULL_INGATED_AT_ORIGIN_RAIL_FACILITY)}
                key={t(FULL_INGATED_AT_ORIGIN_RAIL_FACILITY)}
                heapClassName="shipment-shipmentDetails-editRailIngatedAtOriginFacility heap"
                value={shipment.rail_ingated_at_origin_facility}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_ingated_at_origin_facility"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-ad08dab3"
                className="mb-4 sm:col-span-1"
                title={t(RAIL_CUT_DATE)}
                key={t(RAIL_CUT_DATE)}
                heapClassName="shipment-shipmentDetails-editRailCutDate heap"
                value={shipment.rail_cut_date}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_cut_date"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-d062ec7c"
                className="mb-4 sm:col-span-1"
                title={t(LOADED_AT_RAIL_ORIGIN)}
                key={t(LOADED_AT_RAIL_ORIGIN)}
                heapClassName="shipment-shipmentDetails-editRailLoadedAtOrigin heap"
                value={shipment.rail_loaded_at_origin}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_loaded_at_origin"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-51fe0b9b"
                className="mb-4 sm:col-span-1"
                title={t(DEPARTED_FROM_ORIGIN_RAIL)}
                key={t(DEPARTED_FROM_ORIGIN_RAIL)}
                heapClassName="shipment-shipmentDetails-editRailDepartedFromOrigin heap"
                value={shipment.rail_departed_from_origin}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_departed_from_origin"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-f12055f4"
                className="mb-4 sm:col-span-1"
                title={t(ARRIVED_AT_ORIGIN_RAIL_FACILITY)}
                key={t(ARRIVED_AT_ORIGIN_RAIL_FACILITY)}
                heapClassName="shipment-shipmentDetails-editRailArrivedAtOriginPortFacility heap"
                value={shipment.rail_arrived_at_origin_port_facility}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_arrived_at_origin_port_facility"
                useNamedUpdate
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-c5240fe8"
                className="mb-4 sm:col-span-1"
                title={t(UNLOADED_AT_ORIGIN_PORT)}
                key={t(UNLOADED_AT_ORIGIN_PORT)}
                heapClassName="shipment-shipmentDetails-editRailUnloadedAtOriginPort heap"
                value={shipment.rail_unloaded_at_origin_port}
                editable
                onSave={this.props.onUpdateRailEvent}
                param="rail_unloaded_at_origin_port"
                useNamedUpdate
              />
            </div>
          </CargomaticCollapsibleCard>
        )}
        {this.renderImportantDates()}

        {isIntermodalShipmentType(shipment)
          ? this.getInterModalShipmentOrderAndAdditionalCards()
          : this.getNonInterModalDetailCard()}

        <CargomaticCard
          dataTestId="CargomaticCard-14b65e21"
          className="mb-6"
          header={() => (
            <div
              data-testid="div-ca9c3fa1"
              className="flex items-center justify-between"
            >
              <OverlayTrigger placement="right" overlay={TOOLTIPS.references}>
                <div
                  data-testid="div-3273e7d3"
                  className="flex items-center space-x-1"
                >
                  <h3 data-testid="h3-ec9ec100" className="my-0">
                    {titleize(t('REFERENCE_NUMBERS'))}
                  </h3>
                  <div data-testid="div-d9f06124">
                    <InfoIcon size="medium" />
                  </div>
                </div>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={TOOLTIPS.editReferences}>
                <div data-testid="div-f1082c4d">
                  <EditButton
                    dataTestId="EditButton-4fc88b6f"
                    onClick={this.handleEditReferenceNumbers}
                  />
                </div>
              </OverlayTrigger>
            </div>
          )}
        >
          {isIntermodalType && (
            <div data-testid="div-80758dcc" className="mb-4">
              {this.renderMissingReferenceNumbers()}
            </div>
          )}
          <div data-testid="div-9e93f28c" className="mb-4">
            {this.renderReferenceNumbers()}
          </div>

          <DescriptionListItem
            dataTestId="DescriptionListItem-d2c4ed8b"
            label={t('CUSTOMERS_ESTIMATE')}
            className="mb-4 sm:col-span-1"
          >
            {shipment.estimated_cost
              ? formatMoney(shipment.estimated_cost, shipment.currency)
              : titleize(t('UNKNOWN'))}
          </DescriptionListItem>
          <ShipmentDetailValueItem
            dataTestId="ShipmentDetailValueItem-c5270591"
            rows={4}
            title={titleize(t('NOTE', { count: 3 }))}
            value={unescapeHTML(shipment.notes)}
            editable
            onSave={(value) => this.props.onNotesUpdate(shipment, value)}
          />
          <ShipmentDetailValueItem
            dataTestId="ShipmentDetailValueItem-023a9dce"
            rows={4}
            title={t('ADMIN_NOTES')}
            value={unescapeHTML(shipment.admin_notes)}
            editable
            onSave={(value) => this.props.onAdminNotesUpdate(shipment, value)}
          />
        </CargomaticCard>
        <CargomaticCard
          dataTestId="CargomaticCard-0849d305"
          header={() => (
            <h3 data-testid="h3-2f8852d1" className="my-0">
              {t('TAGS')}
            </h3>
          )}
        >
          <TagEditor
            dataTestId="TagEditor-255f3edb"
            object={shipment}
            tags={shipment.tags}
            suggestions={Constants.shipmentTagSuggestions}
            handleChange={this.props.onTagsUpdate}
          />
        </CargomaticCard>
        <EditReferenceNumbersModal
          reference_numbers={shipment.reference_numbers}
          isIntermodalShipmentType={isIntermodalType}
          show={this.state.showEditReferenceNumbersModal}
          onSave={this.handleSaveReferenceNumbers}
          onClose={this.handleCloseEditReferenceNumbersModal}
          page="ShipmentDetails"
          shipper={shipment.shipper}
          requiredReferenceNumbers={this.state.missingReferenceNumbersList}
        />
      </div>
    );
  }
}

ShipmentDetails.propTypes = {
  shipment: PropTypes.object.isRequired,
  audits: PropTypes.object,
  onReferenceNumbersChange: PropTypes.func.isRequired,
  onNotesUpdate: PropTypes.func.isRequired,
  onAdminNotesUpdate: PropTypes.func.isRequired,
  onTagsUpdate: PropTypes.func.isRequired,
  onUpdateEstimatedArrivalChange: PropTypes.func.isRequired,
  onUpdateArrivalDateChange: PropTypes.func.isRequired,
  onUpdateDischargeDateChange: PropTypes.func.isRequired,
  onUpdateLastFreeDateChange: PropTypes.func.isRequired,
  onUpdateYardLastFreeDateChange: PropTypes.func.isRequired,
  onUpdateEmptyReturnedDateChange: PropTypes.func.isRequired,
  onUpdateEmptyReadyForPickupDateChange: PropTypes.func.isRequired,
  onUpdateLastCutDateChange: PropTypes.func.isRequired,
  onUpdateEarliestReturnDateChange: PropTypes.func.isRequired,
  onUpdateEarliestEmptyDateChange: PropTypes.func.isRequired,
  onUpdateOutgatedDateChange: PropTypes.func.isRequired,
  onShipmentTypeChange: PropTypes.func.isRequired,
  onEquipmentTypeChange: PropTypes.func.isRequired,
  onExceptionExemptChange: PropTypes.func.isRequired,
};

export default withTranslation()(ShipmentDetails);
